import React, { useState } from "react";
import { IoPencilOutline, IoAddOutline, IoTrashOutline, IoChevronUpOutline } from "react-icons/io5";
import { TrendInline } from "./TrendBits";
import type { GroupEditing } from "../../hooks/realtime/useDashboardConfig";
import type { GroupShow } from "../../services/dashboardConfig";
import { useMapPhone } from "../common/RealtimeAreaMap";

const PencilIcon = IoPencilOutline as any;
const AddIcon = IoAddOutline as any;
const TrashIcon = IoTrashOutline as any;
const ChevronIcon = IoChevronUpOutline as any;

// Small cards rendered inside the realtime maps (so they survive fullscreen):
// running totals top-left — with the group editor when a total is being
// edited — and a "last updated" stamp top-right. Presentation only: the caller
// sums whatever it wants a total of and owns the editing state.

export interface TotalRow {
  id?: string; // group id when the row is an editable group
  show?: GroupShow; // which figures this row shows; absent = both
  label: string; // e.g. "Total Oslo"
  present: number | null; // everyone present (null = no data)
  intl?: number; // international, when the board knows it
  delta?: number; // change of `intl` over the board's interval
}

const card = "rounded-xl border border-paper-300/70 bg-surface/95 shadow-soft backdrop-blur";

export const MapTotals: React.FC<{ rows: TotalRow[]; editing?: GroupEditing }> = ({ rows, editing: editingProp }) => {
  const phone = useMapPhone();
  const active = editingProp?.group ?? null;
  // A phone is for reading: no pencil or "Add a total" (a total already being
  // edited still shows its editor, so it can be finished).
  const editing = phone && !active ? undefined : editingProp;
  // Collapsed shows the headline total only — deliberately not remembered:
  // every session starts showing everything on a desktop, and only the
  // headline on a phone, where the full list would cover the map.
  const [collapsed, setCollapsed] = useState(phone);

  if (active && editing) {
    return (
      <div className={`${card} px-4 py-3`}>
        <p className="text-[11px] font-mono uppercase tracking-widest text-ink-faint">Editing total</p>
        <input
          value={active.name}
          onChange={(e) => editing.onRename(e.target.value)}
          placeholder="Name, e.g. Sentrum"
          className="mt-1 w-full rounded-md border border-paper-300 bg-surface px-2 py-1 font-display text-base font-semibold text-ink focus:outline-none focus:ring-2 focus:ring-brand-500/40"
        />
        <p className="mt-2 text-xs text-ink-muted">
          Click zones on the map to add or remove them.{" "}
          <span className="font-medium text-ink">
            {active.areaIds.length} zone{active.areaIds.length === 1 ? "" : "s"}
          </span>{" "}
          in this total.
        </p>
        <div className="mt-3">
          <p className="text-[11px] font-mono uppercase tracking-widest text-ink-faint">Show</p>
          <div className="mt-1 inline-flex w-full rounded-lg border border-paper-300 p-0.5">
            {([
              ["both", "Both"],
              ["present", "People"],
              ["roaming", "Roaming"],
            ] as [GroupShow, string][]).map(([v, label]) => (
              <button
                key={v}
                onClick={() => editing.onSetShow(v)}
                className={`flex-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                  (active.show ?? "both") === v ? "bg-brand-500 text-on-brand" : "text-ink-muted hover:bg-paper-100"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between gap-2">
          <button
            onClick={editing.onDelete}
            className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-bad-ink transition-colors hover:bg-bad-soft"
          >
            <TrashIcon className="h-3.5 w-3.5" /> Delete
          </button>
          <button
            onClick={editing.onDone}
            className="rounded-lg bg-brand-500 px-3 py-1 text-xs font-semibold text-on-brand transition-colors hover:bg-brand-600"
          >
            Done
          </button>
        </div>
        <p className={`mt-2 text-[10px] ${editing.error ? "text-bad-ink" : "text-ink-faint"}`}>
          {editing.error
            ? `Not saved: ${editing.error}`
            : editing.storage === "server"
            ? "Saved to your account."
            : "Saved on this device only."}
        </p>
      </div>
    );
  }

  if (!rows.length && !editing) return null;
  // The overall total is the row with no group id. Collapse always leaves that
  // one visible — never a group, which could be misread as the whole figure.
  const headline = rows.find((r) => !r.id) ?? rows[0];
  if (phone && collapsed && headline) {
    // One line along the bottom of the map: the headline and its trend, tap for the rest.
    return (
      <button
        onClick={() => setCollapsed(false)}
        aria-expanded={false}
        className={`${card} flex w-full items-center gap-3 px-3 py-2 text-left`}
      >
        <span className="min-w-0 flex-1 truncate font-mono text-[11px] uppercase tracking-widest text-ink-muted">{headline.label}</span>
        <HeadlineFigure r={headline} />
        {rows.length > 1 && <span className="whitespace-nowrap text-[11px] text-ink-faint">{rows.length - 1} more</span>}
        <ChevronIcon className="h-3.5 w-3.5 shrink-0 text-ink-faint" />
      </button>
    );
  }
  const shown = collapsed && headline ? [headline] : rows;
  return (
    // w-fit, so the card is only as wide as its numbers — on a map, width costs
    // more than height. The label sits above the figure rather than beside it,
    // which keeps the column slim.
    <div className={`${card} ${phone ? "w-full" : "w-fit self-start"} px-3 py-2`}>
      <ul className="divide-y divide-paper-200">
        {shown.map((r, i) => (
          <li
            key={r.id ?? r.label}
            className={`py-1.5 first:pt-0 last:pb-0 ${!r.id && shown.length > 1 ? "border-b-2 border-paper-300" : ""}`}
          >
            <div className="flex items-center gap-2">
              <span
                className={`min-w-0 flex-1 truncate font-mono uppercase tracking-widest ${
                  r.id ? "text-[10px] text-ink-faint" : "text-[11px] text-ink-muted"
                }`}
              >
                {r.label}
              </span>
              {!collapsed && r.id && editing && (
                <button
                  onClick={() => editing.onEdit(r.id!)}
                  aria-label={`Edit ${r.label}`}
                  title="Edit which zones count"
                  className="-mr-0.5 shrink-0 rounded p-0.5 text-ink-faint transition-colors hover:bg-paper-100 hover:text-ink"
                >
                  <PencilIcon className="h-3 w-3" />
                </button>
              )}
            </div>
            <div className="flex items-baseline gap-1.5 whitespace-nowrap">
              {r.show !== "roaming" && (
                <span className={`font-display font-bold leading-tight tabular-nums text-ink ${r.id ? "text-lg" : "text-2xl"}`}>
                  {r.present != null ? r.present.toLocaleString() : "—"}
                </span>
              )}
              {r.intl != null && r.show !== "present" && (
                <span
                  className={
                    r.show === "roaming"
                      ? `font-display font-bold leading-tight tabular-nums text-ink ${r.id ? "text-lg" : "text-2xl"}`
                      : "text-[11px] tabular-nums text-ink-muted"
                  }
                  title={`${r.intl.toLocaleString()} international`}
                >
                  {r.intl.toLocaleString()}
                  {r.show === "roaming" ? "" : " intl"}
                </span>
              )}
              {r.delta != null && r.show !== "present" && <TrendInline v={r.delta} />}
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-1 flex items-center gap-2 border-t border-paper-200 pt-1">
        {!collapsed && editing && (
          <button
            onClick={editing.onAdd}
            className="inline-flex flex-1 items-center justify-center gap-1 whitespace-nowrap rounded px-2 py-0.5 text-[11px] font-medium text-ink-muted transition-colors hover:bg-paper-100 hover:text-ink"
          >
            <AddIcon className="h-3 w-3" /> Add a total
          </button>
        )}
        <button
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          title={collapsed ? "Show all totals" : "Show only the first total"}
          aria-label={collapsed ? "Show all totals" : "Show only the first total"}
          className={`inline-flex items-center justify-center gap-1 whitespace-nowrap rounded px-2 py-0.5 text-[11px] text-ink-faint transition-colors hover:bg-paper-100 hover:text-ink ${
            collapsed ? "flex-1" : ""
          }`}
        >
          {collapsed && rows.length > 1 && <span>{rows.length - 1} more</span>}
          {/* The phone sheet grows upwards, so its arrows point the other way. */}
          <ChevronIcon className={`h-3 w-3 transition-transform ${collapsed !== phone ? "rotate-180" : ""}`} />
        </button>
      </div>
    </div>
  );
};

/** The headline's figure on one line, for the collapsed phone bar. */
const HeadlineFigure: React.FC<{ r: TotalRow }> = ({ r }) => (
  <span className="flex items-baseline gap-1.5 whitespace-nowrap">
    <span className="font-display text-xl font-bold leading-tight tabular-nums text-ink">
      {r.show === "roaming" ? (r.intl != null ? r.intl.toLocaleString() : "—") : r.present != null ? r.present.toLocaleString() : "—"}
    </span>
    {r.intl != null && r.show !== "present" && r.show !== "roaming" && (
      <span className="text-[11px] tabular-nums text-ink-muted">{r.intl.toLocaleString()} intl</span>
    )}
    {r.delta != null && r.show !== "present" && <TrendInline v={r.delta} />}
  </span>
);

export const MapStamp: React.FC<{ at: number | null; label?: string }> = ({ at, label = "Updated" }) => (
  <div className={`${card} px-2.5 py-1 text-xs text-ink-muted`}>
    {label}{" "}
    <span className="font-medium tabular-nums text-ink">{at ? new Date(at).toLocaleTimeString() : "—"}</span>
  </div>
);

export type MapMode = "present" | "international";

/** What the map's pills and colours show. Sits inside the map so it works in fullscreen. */
export const MapModeSwitch: React.FC<{
  mode: MapMode;
  onChange: (m: MapMode) => void;
  internationalAvailable: boolean;
  loading?: boolean;
}> = ({ mode, onChange, internationalAvailable, loading }) => (
  <div className={`${card} flex items-center gap-2 px-2 py-1.5`}>
    <div className="inline-flex rounded-lg border border-paper-300 p-0.5">
      {(["present", "international"] as const).map((m) => (
        <button
          key={m}
          onClick={() => onChange(m)}
          disabled={m === "international" && !internationalAvailable}
          title={m === "international" && !internationalAvailable ? "Country breakdown isn't enabled for this account" : undefined}
          className={`rounded-md px-2.5 py-0.5 text-xs font-medium transition-colors disabled:opacity-40 ${
            mode === m ? "bg-brand-500 text-on-brand" : "text-ink-muted hover:bg-paper-100"
          }`}
        >
          {m === "present" ? "Present" : "International"}
        </button>
      ))}
    </div>
    <span className="hidden items-center gap-2 text-[11px] text-ink-faint sm:flex" title="Colour = change over the interval: within ±3% (or ±3 people) counts as steady">
      <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-ok" />rising</span>
      <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-warn" />steady</span>
      <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-bad" />falling</span>
      <span className="text-ink-faint/70">· {mode === "present" ? "vs 1h ago" : loading ? "loading…" : "vs interval"}</span>
    </span>
  </div>
);

